/**
 * The map's views as filters on the map graph, applied before buildScene (phase C of
 * specs/tki-kuutio-flow.md → Renderer). The layered renderer rebuilt these inside its
 * layout; here each is a pure function graph → graph, so the scene draws a view the
 * same way it draws the whole map, and the invariants hold on every view.
 *
 *   focus      'loops' | 'blockers' | 'cross' — only what takes part, plus the chains
 *              back to the start, so the picture still hangs together
 *   chain      a relation's chain (two ids, or a loop's ring), laid out as one row
 *   progress   Edistyminen: starts, goals, milestones and targets, re-hung on the
 *              nearest kept ancestor; connections, blockers and loops off
 *   hideBlocked "what gets through": nothing downstream of a blocked connector
 *   globalLinks off: no connections beyond the tree
 *
 * Nothing here is stored: a view is derived, never authored (map-graph rule 6).
 */

// A row uuid names a node card `n:<uuid>` in the map graph; an id already in the graph is itself.
const resolve = (id, has) => (has(id) ? id : (has(`n:${id}`) ? `n:${id}` : null));
const live = (x) => x.state !== 'rejected';

function index(graph) {
  const nodes = (graph.nodes || []).filter(live);
  const edges = (graph.edges || []).filter(live);
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const primaryInto = new Map();
  edges.forEach((e) => { if (e.attrs.primary && !primaryInto.has(e.attrs.to)) primaryInto.set(e.attrs.to, e); });
  const edgeById = new Map(edges.map((e) => [e.id, e]));
  return { nodes, edges, nodeById, primaryInto, edgeById };
}

/** The graph cut to `keep` (node ids): edges between kept ends, loops whole, marks on kept cards. */
function cutTo(graph, keep) {
  const scenarioIds = new Set((graph.scenarios || []).map((s) => s.id));
  const endOk = (id) => keep.has(id) || scenarioIds.has(id);
  return {
    ...graph,
    nodes: (graph.nodes || []).filter((n) => keep.has(n.id)),
    edges: (graph.edges || []).filter((e) => endOk(e.attrs.from) && keep.has(e.attrs.to)),
    loops: (graph.loops || []).filter((l) => (l.attrs?.nodes || []).every((id) => keep.has(id))),
    marks: (graph.marks || []).filter((m) => keep.has(m.attrs?.on)),
  };
}

/** Walk each seed up its primary edges to the start; everything on the way is kept. */
function withChainsToStart(seeds, primaryInto) {
  const keep = new Set();
  seeds.forEach((id) => {
    let cur = id;
    let guard = 0;
    while (cur && !cur.startsWith('s:') && !keep.has(cur) && guard < 80) {
      keep.add(cur);
      cur = primaryInto.get(cur)?.attrs.from || null;
      guard += 1;
    }
  });
  return keep;
}

export function focusGraph(graph, focus) {
  if (!focus) return graph;
  const { nodes, edges, edgeById, primaryInto } = index(graph);
  const seeds = new Set();
  if (focus === 'loops') {
    (graph.loops || []).filter(live).forEach((l) => (l.attrs?.nodes || []).forEach((id) => seeds.add(id)));
  } else if (focus === 'blockers') {
    nodes.filter((n) => n.attrs.kind === 'blocker' || n.attrs.kind === 'avoider').forEach((n) => {
      seeds.add(n.id);
      const on = edgeById.get(n.attrs.on);
      if (on) { seeds.add(on.attrs.from); seeds.add(on.attrs.to); }
    });
  } else if (focus === 'cross') {
    edges.filter((e) => !e.attrs.primary).forEach((e) => { seeds.add(e.attrs.from); seeds.add(e.attrs.to); });
  }
  if (!seeds.size) return cutTo(graph, new Set());
  return cutTo(graph, withChainsToStart(seeds, primaryInto));
}

/**
 * One relation's chain as a row: the ids in order, each hung from the one before by
 * the edge between them (or a plain leads_to), the first from its start; other edges
 * among them (a loop's closing link) stay, so the arc is drawn back over the row.
 * @param {string[]} chainIds  row uuids, in order (a loop: from the card the closing link lands on)
 */
export function chainGraph(graph, chainIds) {
  const { nodeById, edges } = index(graph);
  const known = (chainIds || []).map((id) => resolve(id, (x) => nodeById.has(x))).filter(Boolean);
  if (known.length < 2) return graph;
  const lane = nodeById.get(known[0]).attrs.scenario || null;
  const keep = new Set(known);
  const between = (a, b) => edges.find((e) => e.attrs.from === a && e.attrs.to === b);
  const rowEdges = known.map((id, i) => {
    const from = i === 0 ? lane : known[i - 1];
    const existing = i > 0 ? between(known[i - 1], id) : null;
    return { id: `row:${id}`, kind: 'edge', attrs: { from, to: id, type: existing?.attrs.type || 'leads_to', primary: true }, claims: existing?.claims || [], state: 'accepted' };
  }).filter((e) => e.attrs.from);
  const consecutive = new Set(rowEdges.map((e) => `${e.attrs.from}|${e.attrs.to}`));
  const others = edges.filter((e) => keep.has(e.attrs.from) && keep.has(e.attrs.to) && !consecutive.has(`${e.attrs.from}|${e.attrs.to}`))
    .map((e) => ({ ...e, attrs: { ...e.attrs, primary: false } }));
  return {
    ...graph,
    nodes: known.map((id) => ({ ...nodeById.get(id), attrs: { ...nodeById.get(id).attrs, scenario: lane } })),
    edges: [...rowEdges, ...others],
    loops: (graph.loops || []).filter((l) => (l.attrs?.nodes || []).every((id) => keep.has(id))),
    marks: (graph.marks || []).filter((m) => keep.has(m.attrs?.on)),
  };
}

/** Edistyminen: starts, goals, milestone cards and targets; each re-hung on its nearest kept ancestor. */
export function progressGraph(graph) {
  const { nodes, primaryInto } = index(graph);
  const milestoneOn = new Set((graph.marks || []).filter((m) => live(m) && m.attrs?.type === 'milestone').map((m) => m.attrs.on));
  const kept = nodes.filter((n) => n.attrs.kind === 'goal' || milestoneOn.has(n.id) || n.attrs.level);
  const keep = new Set(kept.map((n) => n.id));
  const nearestKept = (id) => {
    let cur = primaryInto.get(id)?.attrs.from || null;
    let guard = 0;
    while (cur && !cur.startsWith('s:') && !keep.has(cur) && guard < 80) { cur = primaryInto.get(cur)?.attrs.from || null; guard += 1; }
    return cur;
  };
  const edges = [];
  kept.forEach((n) => {
    if (n.attrs.kind === 'goal' && !primaryInto.has(n.id)) return; // a goal hangs from nothing; its claimed edges follow
    const from = nearestKept(n.id);
    if (from) edges.push({ id: `progress:${n.id}`, kind: 'edge', attrs: { from, to: n.id, type: primaryInto.get(n.id)?.attrs.type || 'leads_to', primary: true }, claims: [], state: 'accepted' });
  });
  // Claimed edges into goals and targets stay when both ends are kept
  (graph.edges || []).filter(live).forEach((e) => {
    if (!e.attrs.primary && keep.has(e.attrs.from) && keep.has(e.attrs.to)) edges.push(e);
  });
  return { ...graph, nodes: kept, edges, loops: [], marks: (graph.marks || []).filter((m) => keep.has(m.attrs?.on)) };
}

/** What gets through: a blocked connector's target and everything hanging from it are gone. */
export function hideBlockedGraph(graph) {
  const { nodes, edges, edgeById } = index(graph);
  const blocked = new Set();
  nodes.filter((n) => n.attrs.kind === 'blocker' && n.attrs.on).forEach((n) => {
    const on = edgeById.get(n.attrs.on);
    if (on) blocked.add(on.attrs.to);
  });
  if (!blocked.size) return graph;
  const children = new Map();
  edges.forEach((e) => { if (e.attrs.primary) { if (!children.has(e.attrs.from)) children.set(e.attrs.from, []); children.get(e.attrs.from).push(e.attrs.to); } });
  const gone = new Set();
  const stack = [...blocked];
  while (stack.length) { const id = stack.pop(); if (gone.has(id)) continue; gone.add(id); (children.get(id) || []).forEach((c) => stack.push(c)); }
  const keep = new Set(nodes.filter((n) => !gone.has(n.id)).map((n) => n.id));
  return cutTo(graph, keep);
}

/** No connections beyond the tree. */
export function treeOnlyGraph(graph) {
  return { ...graph, edges: (graph.edges || []).filter((e) => e.attrs.primary) };
}

/**
 * One scenario's view (Skenaario, and Omat inside it): its lane alone, with the shared
 * targets and the end state its goals lead to. Links to other lanes' cards are cut
 * with those cards. An unknown scenario id leaves the whole map.
 * @param {string} scenarioId  a scenario row uuid, or its graph id `s:<uuid>`
 */
export function scenarioGraph(graph, scenarioId) {
  const scenarios = (graph.scenarios || []);
  const sid = scenarioId && (scenarios.some((s) => s.id === scenarioId) ? scenarioId : (scenarios.some((s) => s.id === `s:${scenarioId}`) ? `s:${scenarioId}` : null));
  if (!sid) return graph;
  const { nodes } = index(graph);
  const isTarget = (n) => !n.attrs.scenario && n.attrs.kind === 'goal' && (n.attrs.level === 2 || n.attrs.level === 3);
  const keep = new Set(nodes.filter((n) => n.attrs.scenario === sid || isTarget(n)).map((n) => n.id));
  return cutTo({ ...graph, scenarios: scenarios.filter((s) => s.id === sid) }, keep);
}

/** The view, in the order the layered renderer applied them. */
export function viewGraph(graph, { scenario = null, focus = null, chain = null, progress = false, hideBlocked = false, globalLinks = true } = {}) {
  if (!graph) return graph;
  let g = graph;
  if (scenario) g = scenarioGraph(g, scenario);
  if (chain?.length >= 2) return chainGraph(g, chain);
  if (progress) g = progressGraph(g);
  if (focus) g = focusGraph(g, focus);
  if (hideBlocked) g = hideBlockedGraph(g);
  if (!globalLinks) g = treeOnlyGraph(g);
  return g;
}

/**
 * A box around each choice point: the fork card and its direct children on the tree,
 * with the tally of wanted and unwanted among them (§4b). From the scene, so the
 * box is where the cards are.
 * @param {object} scene  buildScene's result
 * @param {Iterable<string>} parentIds  row uuids of the forks (config/valence detectChoicePoints)
 */
export function choiceBoxes(scene, parentIds, pad = 18) {
  if (!scene || !parentIds) return [];
  const cardById = new Map(scene.cards.map((c) => [c.id, c]));
  const childrenOf = new Map();
  scene.edges.forEach((e) => { if (e.tree) { if (!childrenOf.has(e.from)) childrenOf.set(e.from, []); childrenOf.get(e.from).push(e.to); } });
  const out = [];
  for (const uuid of parentIds) {
    const parent = cardById.get(resolve(uuid, (x) => cardById.has(x)));
    if (!parent) continue;
    const kids = (childrenOf.get(parent.id) || []).map((id) => cardById.get(id)).filter(Boolean);
    if (!kids.length) continue;
    const all = [parent, ...kids];
    const x = Math.min(...all.map((c) => c.x)) - pad;
    const y = Math.min(...all.map((c) => c.y)) - pad;
    const right = Math.max(...all.map((c) => c.x + c.w)) + pad;
    const bottom = Math.max(...all.map((c) => c.y + c.h)) + pad;
    out.push({ id: parent.id, x, y, w: right - x, h: bottom - y, up: kids.filter((k) => k.valence === 'wanted').length, down: kids.filter((k) => k.valence === 'unwanted').length });
  }
  return out;
}
